// Houdt de prijzen in het abonnement-keuzeblok (blocks/ab-subscription-options)
// in sync met de gekozen variant. Het blok rendert server-side vanaf de op dat
// moment geselecteerde variant; bij een variantwissel morpht het theme alleen
// prijs/galerij/knoppen — dit component ververst de bedragen in de kaarten
// (eenmalig, frequentie-tiles, bundel-staffel) uit de meegeleverde HTML.
// Alleen tekstuele prijselementen worden vervangen, zodat radio-state en de
// listeners van het inline blokscript intact blijven.
import { ThemeEvents } from "@theme/events";

class AbSoComponent extends HTMLElement {
  #abortController = null;

  connectedCallback() {
    const section = this.closest(".shopify-section, dialog");
    if (!section) return;
    this.#abortController = new AbortController();
    section.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, {
      signal: this.#abortController.signal,
    });
  }

  disconnectedCallback() {
    this.#abortController?.abort();
  }

  /**
   * @param {CustomEvent} event - VariantUpdateEvent met event.detail.data.html
   *   (volledige document-parse van de PDP voor de nieuwe variant, of null bij
   *   een cache-hit — de background-sync die daarop volgt levert wél HTML).
   */
  #onVariantUpdate = (event) => {
    // product-form.js enabled de winkelmand-knop bij een beschikbare variant;
    // daarna de keuze-gate (eenmalig/abonnement) uit het blokscript opnieuw
    // toepassen, met de beschikbaarheid zodat de knop na de keuze de juiste
    // state krijgt. rAF zodat dit ná de listener van product-form.js draait.
    const available = event.detail?.resource?.available;
    requestAnimationFrame(() =>
      this.querySelector(".ab-so")?.abSoApplyGate?.(typeof available === "boolean" ? available : undefined)
    );

    const html = event.detail?.data?.html;
    if (!html) return;

    const handle = this.dataset.productHandle;
    const fresh =
      html.querySelector(`ab-so-component[data-product-handle="${CSS.escape(handle)}"]`) ||
      html.querySelector("ab-so-component");
    if (!fresh) return;

    this.#syncText(fresh, ".ab-so__price");
    this.#syncText(fresh, ".ab-so__discount");
    this.#syncTiles(fresh);
    this.#syncBundles(fresh);
    this.#syncGiftPlan(fresh);
  };

  /**
   * Het cadeau hangt aan de beste frequentie (data-gift-plan-id); die kan per
   * variant verschillen als de variant een eigen selling-plan-groep heeft.
   * @param {Element} fresh - Vers gerenderd ab-so-component
   */
  #syncGiftPlan(fresh) {
    const current = this.querySelector("ab-cadeau-component");
    const next = fresh.querySelector("ab-cadeau-component");
    if (current && next && next.dataset.giftPlanId && current.dataset.giftPlanId !== next.dataset.giftPlanId) {
      current.dataset.giftPlanId = next.dataset.giftPlanId;
    }
  }

  /**
   * Kopieert de tekst van één element (bv. de eenmalig-prijs) uit de verse render.
   * @param {Element} fresh - Vers gerenderd ab-so-component
   * @param {string} selector - Selector binnen het blok
   */
  #syncText(fresh, selector) {
    const current = this.querySelector(selector);
    const next = fresh.querySelector(selector);
    if (current && next && current.textContent !== next.textContent) {
      current.textContent = next.textContent;
    }
  }

  // Frequentie-tiles matchen op selling-plan-id (radio value). Naast de prijs
  // syncen ook label, subtekst en aanbevolen aantal mee. Heeft de nieuwe
  // variant andere plannen (Firmhouse koppelt per variant een eigen groep,
  // bv. Astaxanthine 60 vs 120 softgels), dan worden de tiles in hun geheel
  // vervangen; het blokscript luistert via delegatie, dus de nieuwe radio's
  // werken direct.
  /** @param {Element} fresh - Vers gerenderd ab-so-component */
  #syncTiles(fresh) {
    const freshInputs = [...fresh.querySelectorAll('input[name^="ab-frequency-"]')];
    const currentInputs = [...this.querySelectorAll('input[name^="ab-frequency-"]')];
    const planIds = (inputs) => inputs.map((i) => i.value).sort().join(",");

    if (planIds(freshInputs) !== planIds(currentInputs)) {
      const currentTiles = this.querySelector(".ab-so__tiles");
      const freshTiles = fresh.querySelector(".ab-so__tiles");
      if (currentTiles && freshTiles) {
        // Eerder gekozen plan behouden als de nieuwe variant het ook heeft,
        // anders de aanbevolen frequentie (server-side checked).
        const chosen = currentInputs.find((i) => i.checked)?.value;
        currentTiles.innerHTML = freshTiles.innerHTML;
        if (chosen) {
          const keep = currentTiles.querySelector(`input[name^="ab-frequency-"][value="${CSS.escape(chosen)}"]`);
          if (keep) keep.checked = true;
        }
        const freqPanel = this.querySelector("[data-ab-freq]");
        if (freqPanel && !freqPanel.hidden) this.querySelector(".ab-so")?.abSoSyncFrequency?.();
      }
      return;
    }

    for (const input of currentInputs) {
      const freshInput = freshInputs.find((candidate) => candidate.value === input.value);
      const current = input.closest(".ab-so__tile");
      const next = freshInput?.closest(".ab-so__tile");
      if (!current || !next) continue;

      input.dataset.qty = freshInput.dataset.qty || "1";
      for (const selector of [".ab-so__tile-label", ".ab-so__tile-sub", ".ab-so__tile-price"]) {
        const currentPart = current.querySelector(selector);
        const nextPart = next.querySelector(selector);
        if (currentPart && nextPart && currentPart.innerHTML !== nextPart.innerHTML) {
          currentPart.innerHTML = nextPart.innerHTML;
        }
      }
    }

    // Gewijzigd aanbevolen aantal doorzetten naar het productformulier, maar
    // alleen als er al een frequentie gekozen is (abonneer-kaart open).
    const checked = this.querySelector('input[name^="ab-frequency-"]:checked');
    const freqPanel = this.querySelector("[data-ab-freq]");
    if (checked && freqPanel && !freqPanel.hidden) {
      const root = this.querySelector(".ab-so");
      const qty = checked.dataset.qty || "1";
      if (root?.abSoSetQty) root.abSoSetQty(qty);
      else {
        const qtyInput = this.querySelector("[data-ab-quantity]");
        if (qtyInput) qtyInput.value = qty;
      }
    }
  }

  // Bundel-rijen matchen op aantal (radio value); alleen het prijsgedeelte
  // (.ab-so__bundle-price) wordt vervangen.
  /** @param {Element} fresh - Vers gerenderd ab-so-component */
  #syncBundles(fresh) {
    for (const input of this.querySelectorAll('input[name^="ab-bundle-"]')) {
      const freshInput = [...fresh.querySelectorAll('input[name^="ab-bundle-"]')].find(
        (candidate) => candidate.value === input.value
      );
      const current = input.closest(".ab-so__bundle")?.querySelector(".ab-so__bundle-price");
      const next = freshInput?.closest(".ab-so__bundle")?.querySelector(".ab-so__bundle-price");
      if (current && next && current.innerHTML !== next.innerHTML) {
        current.innerHTML = next.innerHTML;
      }
    }
  }
}

if (!customElements.get("ab-so-component")) {
  customElements.define("ab-so-component", AbSoComponent);
}
